import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Plus, Trash2, Pencil, Users, MapPin, Building2, Crown, Globe, Briefcase, Tag, Sparkles } from 'lucide-react';
import SmartRuleBuilder from './common/SmartRuleBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GROUP_TYPES = [
  { value: 'all', label: 'All Groups', icon: Tag },
  { value: 'brand', label: 'Brand', icon: Crown },
  { value: 'sub_brand', label: 'Sub-Brand', icon: Crown },
  { value: 'region', label: 'Region', icon: Globe },
  { value: 'ownership', label: 'Ownership', icon: Building2 },
  { value: 'management', label: 'Management', icon: Briefcase },
  { value: 'custom', label: 'Custom', icon: Tag },
];

const TYPE_COLORS = {
  brand: 'bg-purple-100 text-purple-700',
  sub_brand: 'bg-purple-50 text-purple-600',
  region: 'bg-sky-100 text-sky-700',
  ownership: 'bg-emerald-100 text-emerald-700',
  management: 'bg-amber-100 text-amber-700',
  custom: 'bg-gray-100 text-gray-600',
};

export default function PropertyGroups({ token, role }) {
  const [groups, setGroups] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState({ name: '', group_type: 'custom', parent_group_id: '', description: '', color: '#6366F1', property_ids: [], smart_rule: null });
  const [filterType, setFilterType] = useState('all');
  const [membership, setMembership] = useState('manual'); // 'manual' | 'smart'

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = role === 'admin';

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const [gRes, pRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/property-groups`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
      ]);
      setGroups(gRes.data.groups || []);
      setAllProperties(pRes.data || []);
    } catch { toast.error('Failed to load groups'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchGroups(); }, []);

  const filtered = filterType === 'all' ? groups : groups.filter(g => g.group_type === filterType);
  const brandGroups = groups.filter(g => g.group_type === 'brand');

  const saveGroup = async () => {
    const payload = { ...form };
    if (membership === 'smart') {
      // Smart membership: send the rule; ignore any manual property_ids (backend recomputes)
      payload.property_ids = [];
    } else {
      payload.smart_rule = null;
    }
    try {
      if (editingGroup) {
        await axios.put(`${BACKEND_URL}/api/admin/property-groups/${editingGroup.id}`, payload, { headers });
        toast.success('Group updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/property-groups`, payload, { headers });
        toast.success('Group created');
      }
      setShowNew(false);
      setEditingGroup(null);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/property-groups/${groupId}`, { headers });
      toast.success('Group deleted');
      fetchGroups();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleProperty = (propId) => {
    setForm(prev => ({
      ...prev,
      property_ids: prev.property_ids.includes(propId) ? prev.property_ids.filter(id => id !== propId) : [...prev.property_ids, propId],
    }));
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="property-groups">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Property Groups</h2>
          <p className="text-sm text-muted-foreground">Organize properties by brand, region, ownership, or management</p>
        </div>
        {isAdmin && (
          <Button size="sm" className="gap-1.5 text-xs" onClick={() => {
            setEditingGroup(null);
            setForm({ name: '', group_type: 'custom', parent_group_id: '', description: '', color: '#6366F1', property_ids: [], smart_rule: null });
            setMembership('manual');
            setShowNew(true);
          }} data-testid="add-group-button">
            <Plus className="h-3.5 w-3.5" /> New Group
          </Button>
        )}
      </div>

      {/* Type filter tabs */}
      <div className="flex flex-wrap gap-1.5">
        {GROUP_TYPES.map(t => {
          const Icon = t.icon;
          const count = t.value === 'all' ? groups.length : groups.filter(g => g.group_type === t.value).length;
          if (count === 0 && t.value !== 'all') return null;
          return (
            <button
              key={t.value}
              onClick={() => setFilterType(t.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filterType === t.value ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon className="h-3 w-3" /> {t.label} <span className="font-mono ml-0.5">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="groups-grid">
          {filtered.map(g => {
            const subBrands = groups.filter(sg => sg.parent_group_id === g.id);
            return (
              <div key={g.id} className="border rounded-xl p-4 bg-card hover:shadow-sm transition-shadow group/card">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                  <h3 className="text-sm font-semibold flex-1 flex items-center gap-1.5">
                    {g.name}
                    {g.smart_rule && g.is_smart && (
                      <span className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded" title="Smart group — membership auto-computed from rule">
                        <Sparkles className="h-2.5 w-2.5" /> Smart
                      </span>
                    )}
                  </h3>
                  <Badge className={`text-[9px] ${TYPE_COLORS[g.group_type] || TYPE_COLORS.custom}`}>{g.group_type?.replace('_', ' ')}</Badge>
                  <Badge variant="secondary" className="text-[10px] font-mono">{g.property_ids?.length || 0}</Badge>
                  {isAdmin && (
                    <div className="flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => {
                        setEditingGroup(g);
                        setForm({ name: g.name, group_type: g.group_type || 'custom', parent_group_id: g.parent_group_id || '', description: g.description || '', color: g.color || '#6366F1', property_ids: g.property_ids || [], smart_rule: g.smart_rule || null });
                        setMembership(g.smart_rule ? 'smart' : 'manual');
                        setShowNew(true);
                      }}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteGroup(g.id)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
                {g.parent_group_name && <p className="text-[10px] text-muted-foreground mb-1">Parent: {g.parent_group_name}</p>}
                {g.description && <p className="text-xs text-muted-foreground mb-2">{g.description}</p>}
                <div className="flex flex-wrap gap-1">
                  {(g.properties || []).slice(0, 5).map(p => (
                    <Badge key={p.id} variant="outline" className="text-[10px]">{p.name}</Badge>
                  ))}
                  {(g.properties || []).length > 5 && <Badge variant="outline" className="text-[10px] text-muted-foreground">+{g.properties.length - 5} more</Badge>}
                </div>
                {/* Sub-brands */}
                {subBrands.length > 0 && (
                  <div className="mt-3 pt-3 border-t space-y-1.5">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Sub-Brands</span>
                    {subBrands.map(sb => (
                      <div key={sb.id} className="flex items-center gap-2 text-xs pl-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: sb.color }} />
                        <span className="font-medium">{sb.name}</span>
                        <Badge variant="secondary" className="text-[9px] font-mono">{sb.property_ids?.length || 0}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Group Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Group' : 'New Property Group'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Group Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Gulf Coast" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.group_type} onValueChange={v => setForm(p => ({ ...p, group_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brand">Brand</SelectItem>
                    <SelectItem value="sub_brand">Sub-Brand</SelectItem>
                    <SelectItem value="region">Region</SelectItem>
                    <SelectItem value="ownership">Ownership</SelectItem>
                    <SelectItem value="management">Management</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.group_type === 'sub_brand' && (
              <div>
                <Label className="text-xs">Parent Brand</Label>
                <Select value={form.parent_group_id || ''} onValueChange={v => setForm(p => ({ ...p, parent_group_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select parent brand..." /></SelectTrigger>
                  <SelectContent>
                    {brandGroups.map(bg => <SelectItem key={bg.id} value={bg.id}>{bg.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional" />
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="w-full h-9 rounded cursor-pointer" />
              </div>
            </div>
            <Label className="text-xs">Membership</Label>
            <div className="flex rounded-lg border overflow-hidden text-xs" data-testid="pg-membership-toggle">
              <button
                type="button"
                onClick={() => setMembership('manual')}
                className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 font-medium ${membership === 'manual' ? 'bg-primary text-primary-foreground' : 'bg-muted/40 text-muted-foreground hover:bg-muted'}`}
                data-testid="pg-membership-manual"
              >
                <Users className="h-3 w-3" /> Manual list
              </button>
              <button
                type="button"
                onClick={() => setMembership('smart')}
                className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 font-medium ${membership === 'smart' ? 'bg-primary text-primary-foreground' : 'bg-muted/40 text-muted-foreground hover:bg-muted'}`}
                data-testid="pg-membership-smart"
              >
                <Sparkles className="h-3 w-3" /> Smart rule
              </button>
            </div>
            {membership === 'manual' ? (
              <Label className="text-xs">Properties ({form.property_ids.length})</Label>
            ) : null}
          </div>
          {membership === 'manual' ? (
            <ScrollArea className="flex-1 -mx-6 px-6 max-h-[250px]">
              <div className="space-y-1">
                {allProperties.map(p => {
                  const sel = form.property_ids.includes(p.id);
                  return (
                    <label key={p.id} className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer ${sel ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-muted/40'}`}>
                      <input type="checkbox" checked={sel} onChange={() => toggleProperty(p.id)} className="rounded" />
                      <span className="text-sm">{p.name}</span>
                    </label>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <ScrollArea className="flex-1 -mx-6 px-6 max-h-[320px]">
              <SmartRuleBuilder
                token={token}
                value={form.smart_rule || { all: [] }}
                onChange={(v) => setForm(p => ({ ...p, smart_rule: v }))}
              />
            </ScrollArea>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={saveGroup} disabled={!form.name}>
              {editingGroup ? 'Update Group' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
